import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, TrendingUp, Palette, Target, Users, Layers, Monitor, ChevronDown, ChevronUp, Megaphone, PenTool, Building2, Store, Hotel, Sofa } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEOHead from '../components/SEOHead';

const TextReveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={isInView ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      >{children}</motion.div>
    </div>
  );
};

const AnimatedNumber = ({ value, suffix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (isInView) {
      const end = parseInt(value);
      const start = Date.now();
      const timer = setInterval(() => {
        const p = Math.min((Date.now() - start) / 1800, 1);
        setCount(Math.floor((1 - Math.pow(1 - p, 4)) * end));
        if (p >= 1) clearInterval(timer);
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  return <span ref={ref}>{count}{suffix}</span>;
};

const FAQItem = ({ q, a, open, onClick }) => (
  <div className="border-b border-[#FF6B35]/10 last:border-0">
    <button onClick={onClick} className="w-full py-5 flex items-center justify-between text-left group" aria-expanded={open}>
      <span className="text-[#2D2D2D] font-medium pr-4 group-hover:text-[#FF6B35] transition-colors">{q}</span>
      {open ? <ChevronUp size={18} className="text-[#FF6B35] flex-shrink-0" /> : <ChevronDown size={18} className="text-[#8A8A8A] flex-shrink-0" />}
    </button>
    <motion.div initial={false} animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }} className="overflow-hidden">
      <p className="pb-5 text-[#5A5A5A] text-sm leading-relaxed">{a}</p>
    </motion.div>
  </div>
);

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const [openFaq, setOpenFaq] = useState(null);

  const digitalServices = [
    { title: 'Brand Strategy', desc: 'Position your brand for long-term dominance with strategic identity, messaging, and market differentiation.', icon: Target, link: '/services/brand-strategy' },
    { title: 'Performance Marketing', desc: 'Data-driven campaigns across Google, Meta, and LinkedIn that deliver measurable ROI and qualified leads.', icon: TrendingUp, link: '/services/performance-marketing' },
    { title: 'Social Media Management', desc: 'Content calendars, community management, and growth strategies across every platform that matters.', icon: Megaphone, link: '/services/social-media-management' },
    { title: 'Content Production', desc: 'SEO-optimized blogs, videos, and creative assets that establish authority and drive organic traffic.', icon: PenTool, link: '/services/content-production' },
    { title: 'Founder Branding', desc: 'Personal brand positioning for founders and CXOs who want to become industry thought leaders.', icon: Users, link: '/services/founder-branding' },
    { title: 'Website Design', desc: 'Conversion-focused websites built with modern tech stacks, optimized for speed and search engines.', icon: Monitor, link: '/services/website-design' },
  ];

  const interiorServices = [
    { title: 'Residential Interiors', desc: 'Thoughtfully designed living spaces that reflect personality and elevate everyday life.', icon: Sofa, link: '/services/residential-interiors' },
    { title: 'Commercial Interiors', desc: 'Office and corporate environments that boost productivity, culture, and brand perception.', icon: Building2, link: '/services/commercial-interiors' },
    { title: 'Workspace Design', desc: 'Modern co-working and private office designs built for collaboration and focus.', icon: Layers, link: '/services/workspace-design' },
    { title: 'Retail Spaces', desc: 'Store and showroom interiors that create immersive shopping experiences and drive conversion.', icon: Store, link: '/services/retail-spaces' },
    { title: 'Hospitality Design', desc: 'Hotel, restaurant, and cafe interiors that tell a story and create lasting guest impressions.', icon: Hotel, link: '/services/hospitality-design' },
  ];

  const whyBcm = [
    { title: 'Creative Strategy', desc: 'Every project starts with strategy. We think before we design, plan before we build.' },
    { title: 'Experience-Driven Design', desc: 'We design for how people interact, not just how things look. Every detail serves a purpose.' },
    { title: 'Multi-Vertical Thinking', desc: 'Digital and physical design expertise means holistic brand experiences, not isolated deliverables.' },
    { title: 'Brand-First Approach', desc: 'Your brand identity drives every decision. Consistency across touchpoints builds trust.' },
    { title: 'Digital + Physical Integration', desc: 'We connect online presence with offline spaces for a unified brand experience.' },
  ];

  const digitalFaqs = [
    { q: 'What industries does BCM work with for digital marketing?', a: 'We work with startups, D2C brands, real estate developers, hospitality businesses, and established enterprises across India, Dubai, and the UAE. Our strategy adapts to your industry vertical and growth stage.' },
    { q: 'How long does it take to see results from digital marketing?', a: 'Performance marketing shows initial traction in 4-6 weeks. SEO and content strategies build compounding authority over 3-6 months. We set clear milestones from day one so you always know where things stand.' },
    { q: 'Do you offer branding and performance marketing together?', a: 'Yes. In fact, we recommend it. A strong brand identity amplifies every performance campaign. Our integrated approach ensures your ads, content, and brand messaging work as a unified growth engine.' },
  ];

  const interiorFaqs = [
    { q: 'Does BCM handle large commercial interior projects?', a: 'Absolutely. We have executed commercial spaces including offices, retail stores, restaurants, and hospitality venues. Our team manages end-to-end design from concept to handover.' },
    { q: 'Can you design brand experience spaces?', a: 'This is what sets us apart. We design spaces that are extensions of your brand identity — from material palettes that match brand colors to spatial layouts that reflect brand values.' },
    { q: 'Do you offer end-to-end execution for interiors?', a: 'Yes. Our service covers initial consultation, concept design, 3D visualization, material sourcing, vendor coordination, project management, and final handover. One team, zero gaps.' },
  ];

  const homeSchemas = [
    { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [...digitalFaqs, ...interiorFaqs].map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) }
  ];

  return (
    <Layout>
      <SEOHead
        title="Buzz Craft Maven | Designing Brands, Spaces, And Experiences"
        description="Buzz Craft Maven is a modern creative consultancy specializing in digital marketing, brand strategy, and interior design solutions for businesses across India and Dubai."
        canonical="https://buzzcraftmaven.com"
        ogTitle="Buzz Craft Maven | Designing Brands, Spaces, And Experiences"
        ogDescription="A modern creative consultancy combining strategic digital growth with thoughtfully designed spaces."
        schemas={homeSchemas}
      />

      {/* SECTION 1: HERO */}
      <section ref={heroRef} className="min-h-[90vh] relative overflow-hidden bg-sunrise-hero flex items-center" aria-label="Hero">
        <div className="absolute inset-0">
          <motion.div className="absolute top-10 right-[10%] w-[400px] h-[400px] sun-orb sun-orb-primary"
            animate={{ scale: [1, 1.08, 1], opacity: [0.35, 0.5, 0.35] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} />
        </div>

        <motion.div className="container mx-auto px-6 md:px-12 pt-32 pb-20 relative z-10" style={{ opacity: heroOpacity }}>
          <div className="max-w-4xl">
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="inline-block px-4 py-1.5 bg-white/70 backdrop-blur-sm text-[#5A5A5A] text-sm font-medium rounded-full mb-8 border border-[#FF6B35]/10">
              Creative Consultancy
            </motion.span>

            <div className="space-y-1 mb-8">
              <TextReveal delay={0.3}>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D2D2D] tracking-tight leading-[1.05]">
                  Designing Brands, Spaces,
                </h1>
              </TextReveal>
              <TextReveal delay={0.4}>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                  <span className="text-gradient-sunrise">And Experiences.</span>
                </h1>
              </TextReveal>
            </div>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="text-base md:text-lg text-[#5A5A5A] max-w-xl leading-relaxed mb-10">
              Digital marketing and interior design solutions crafted for modern businesses and evolving brands.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              className="flex flex-wrap items-center gap-4">
              <Link to="/digital" data-testid="hero-cta-digital">
                <motion.button className="btn-sunrise text-base px-8 py-3.5" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <span className="flex items-center gap-2">Explore Digital <ArrowRight size={18} /></span>
                </motion.button>
              </Link>
              <Link to="/interiors" data-testid="hero-cta-interiors">
                <motion.button className="btn-outline-warm px-8 py-3.5" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  Explore Interiors
                </motion.button>
              </Link>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
              className="flex items-center gap-10 mt-14 pt-8 border-t border-[#FF6B35]/10">
              {[{ v: '150', s: '+', l: 'Projects Delivered' }, { v: '98', s: '%', l: 'Client Satisfaction' }, { v: '3', s: '', l: 'Countries' }].map(st => (
                <div key={st.l}>
                  <p className="font-display text-2xl md:text-3xl text-[#FF6B35] font-bold"><AnimatedNumber value={st.v} suffix={st.s} /></p>
                  <p className="text-[#8A8A8A] text-xs mt-1">{st.l}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* SECTION 2: BRAND POSITIONING */}
      <section className="py-20 md:py-28 bg-white" aria-label="Brand positioning">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <TextReveal>
              <h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-6">
                Two Creative Verticals. <span className="text-gradient-sunrise">One Unified Vision.</span>
              </h2>
            </TextReveal>
            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="text-[#5A5A5A] text-base leading-relaxed max-w-2xl mx-auto">
              Buzz Craft Maven combines strategic digital growth with thoughtfully designed spaces to create stronger brand presence — online and offline. 
              Whether you need a performance marketing engine or a workspace that reflects your culture, we bring the same creative rigor to every project.
            </motion.p>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }}
              className="flex items-center justify-center gap-8 mt-10">
              <Link to="/digital" className="flex items-center gap-2 text-[#FF6B35] font-medium hover:underline">Digital Solutions <ArrowRight size={16} /></Link>
              <span className="w-px h-6 bg-[#FF6B35]/20" />
              <Link to="/interiors" className="flex items-center gap-2 text-[#FF6B35] font-medium hover:underline">Interior Solutions <ArrowRight size={16} /></Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 3: DIGITAL SERVICES */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="Digital services">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <TextReveal><span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-4">Digital Solutions</span></TextReveal>
            <TextReveal delay={0.1}><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">Strategic growth for <span className="text-gradient-sunrise">modern brands.</span></h2></TextReveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {digitalServices.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Link to={s.link} data-testid={`digital-service-${i}`}>
                  <div className="p-6 bg-white rounded-2xl border border-[#FF6B35]/8 hover:border-[#FF6B35]/25 hover:shadow-lg hover:shadow-orange-500/5 transition-all group h-full">
                    <div className="w-11 h-11 rounded-xl bg-[#FF6B35]/8 flex items-center justify-center mb-4 group-hover:bg-[#FF6B35] transition-colors">
                      <s.icon size={20} className="text-[#FF6B35] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-[#2D2D2D] font-semibold mb-2 group-hover:text-[#FF6B35] transition-colors">{s.title}</h3>
                    <p className="text-[#5A5A5A] text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: INTERIOR SERVICES */}
      <section className="py-20 md:py-28 bg-white" aria-label="Interior services">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <TextReveal><span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-4">Interior Solutions</span></TextReveal>
            <TextReveal delay={0.1}><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">Spaces that tell <span className="text-gradient-sunrise">your story.</span></h2></TextReveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {interiorServices.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Link to={s.link} data-testid={`interior-service-${i}`}>
                  <div className="p-6 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/8 hover:border-[#FF6B35]/25 hover:shadow-lg hover:shadow-orange-500/5 transition-all group h-full">
                    <div className="w-11 h-11 rounded-xl bg-[#FF6B35]/8 flex items-center justify-center mb-4 group-hover:bg-[#FF6B35] transition-colors">
                      <s.icon size={20} className="text-[#FF6B35] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-[#2D2D2D] font-semibold mb-2 group-hover:text-[#FF6B35] transition-colors">{s.title}</h3>
                    <p className="text-[#5A5A5A] text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: WHY BCM */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="Why BCM">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-8 lg:gap-16">
            <div className="col-span-12 lg:col-span-5">
              <TextReveal><span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-4">Why BCM</span></TextReveal>
              <TextReveal delay={0.1}><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-2">Why Modern Brands</h2></TextReveal>
              <TextReveal delay={0.2}><h2 className="font-display text-3xl md:text-4xl mb-6"><span className="text-gradient-sunrise">Choose BCM.</span></h2></TextReveal>
              <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
                className="text-[#5A5A5A] leading-relaxed">
                We are not a traditional agency. We are a creative consultancy that thinks in systems — connecting brand strategy, digital performance, and physical spaces into one cohesive growth engine.
              </motion.p>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <div className="space-y-4">
                {whyBcm.map((f, i) => (
                  <motion.div key={f.title} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                    className="p-5 bg-white rounded-2xl border border-[#FF6B35]/8 hover:border-[#FF6B35]/20 transition-all">
                    <h3 className="text-[#2D2D2D] font-semibold mb-1">{f.title}</h3>
                    <p className="text-[#5A5A5A] text-sm">{f.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: CASE STUDIES */}
      <section className="py-20 md:py-28 bg-white" aria-label="Case studies">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <TextReveal><span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-4">Selected Work</span></TextReveal>
            <TextReveal delay={0.1}><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">Results that <span className="text-gradient-sunrise">speak louder.</span></h2></TextReveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'D2C Fashion Brand', challenge: 'Low online visibility, no brand positioning', strategy: 'Full brand identity + performance marketing + social media overhaul', result: '340% increase in qualified traffic, 5X ROAS in 4 months' },
              { title: 'Restaurant Chain — Dubai', challenge: 'Generic interior, poor customer dwell time', strategy: 'Brand-aligned interior redesign + spatial branding + experience mapping', result: '45% increase in average order value, 60% more repeat visits' },
              { title: 'Tech Startup — Chennai', challenge: 'No digital presence, founder had zero personal brand', strategy: 'Website design + founder branding + content strategy + LinkedIn growth', result: '12K LinkedIn followers in 6 months, 3 enterprise clients from inbound' },
              { title: 'Luxury Real Estate — Hyderabad', challenge: 'Premium project with no differentiated marketing', strategy: 'Immersive website + paid campaign + model apartment interior design', result: '78% of inventory sold in pre-launch phase' },
            ].map((cs, i) => (
              <motion.div key={cs.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="p-6 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/8" data-testid={`case-study-${i}`}>
                <h3 className="font-display text-lg font-semibold text-[#2D2D2D] mb-4">{cs.title}</h3>
                <div className="space-y-3 text-sm">
                  <div><span className="text-[#FF6B35] font-medium">Challenge:</span> <span className="text-[#5A5A5A]">{cs.challenge}</span></div>
                  <div><span className="text-[#FF6B35] font-medium">Strategy:</span> <span className="text-[#5A5A5A]">{cs.strategy}</span></div>
                  <div><span className="text-[#FF6B35] font-medium">Result:</span> <span className="text-[#2D2D2D] font-medium">{cs.result}</span></div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/case-studies" className="text-[#FF6B35] font-medium hover:underline inline-flex items-center gap-1">View All Case Studies <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* SECTION 7: FAQ */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="Frequently asked questions">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <TextReveal><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">Frequently Asked <span className="text-gradient-sunrise">Questions</span></h2></TextReveal>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-[#FF6B35] mb-4">Digital Marketing</h3>
              <div className="bg-white rounded-2xl p-6 border border-[#FF6B35]/8">
                {digitalFaqs.map((f, i) => (
                  <FAQItem key={`d${i}`} q={f.q} a={f.a} open={openFaq === `d${i}`} onClick={() => setOpenFaq(openFaq === `d${i}` ? null : `d${i}`)} />
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-[#FF6B35] mb-4">Interior Design</h3>
              <div className="bg-white rounded-2xl p-6 border border-[#FF6B35]/8">
                {interiorFaqs.map((f, i) => (
                  <FAQItem key={`i${i}`} q={f.q} a={f.a} open={openFaq === `i${i}`} onClick={() => setOpenFaq(openFaq === `i${i}` ? null : `i${i}`)} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: CTA */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#FF6B35] to-[#FFB627] relative overflow-hidden" aria-label="Call to action">
        <div className="absolute inset-0">
          <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }}
            animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 10, repeat: Infinity }} />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-4">Ready to elevate your brand?</h2>
          <p className="text-white/80 text-base max-w-lg mx-auto mb-10">Whether it&apos;s digital growth or a space that inspires — let&apos;s create something exceptional together.</p>
          <Link to="/contact" data-testid="cta-contact">
            <motion.button className="px-10 py-4 bg-white text-[#FF6B35] rounded-full font-semibold text-base shadow-xl hover:shadow-2xl transition-shadow inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              Start a Conversation <ArrowRight size={20} />
            </motion.button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
